/**
 * THE single source of truth for every tunable in this demo.
 *
 * Rule (project CLAUDE.md §5): no numeric or string literal that a human might want to change may
 * appear anywhere else in `src/`. An ESLint `no-magic-numbers` rule scoped to `src/**` (excluding
 * this directory) enforces it.
 *
 * Read `doc/plan/01_ar-vending-demo.md` §6 before touching anything in the Target / Machine /
 * Hero-can groups — there are three distinct coordinate spaces and mixing them is the defect that
 * survived the first plan review.
 */

export type FigureId = 'A' | 'B' | 'C';

// ---------------------------------------------------------------------------
// Recognition target — the physical object the camera looks at.
// ---------------------------------------------------------------------------

export const TARGET = {
  /**
   * Real width of the printed target card, in metres. 100 x 150 mm = 4 x 6 inches, the standard
   * photo-print size, so the card can be produced anywhere. Every real-world dimension in the
   * demo scales off this one number — measure the actual print, paper trim varies.
   *
   * The original perforated panel was rejected at T2: MindAR extracted ZERO tracking points from
   * it, because its uniqueness filter discards any patch that correlates >0.95 with a neighbour
   * and a periodic hole lattice fails that everywhere. See plan §13a.
   */
  widthM: 0.1,

  /** Unused for a flat card; kept because the seam expects a physical object. */
  depthM: 0.001,

  /**
   * Pixel width of the compiled target image. THIS DEFINES THE WORLD UNIT.
   * MindAR's anchor postMatrix uses scale = markerWidth (in pixels), so world space is measured
   * in compiled-image pixels. Asserted against the runtime value at session start; a mismatch
   * throws rather than silently shifting every camera-relative calculation.
   */
  imagePxWidth: 1024,

  /**
   * height / width of the compiled target image. Emitted by tools/make_target_card.py and
   * asserted at runtime against the loaded .mind — a recompile with a different crop throws
   * rather than silently shifting the vertical alignment. Exactly 1.5 for a 100 x 150 mm card.
   */
  imageAspect: 1.5,
};

// ---------------------------------------------------------------------------
// Machine placement (anchor-local space: target width = 1.0)
// ---------------------------------------------------------------------------

export const MACHINE = {
  /**
   * Rendered width of the vending machine in metres. The GLB is 1.30 m wide natively.
   * Default 0.10 = exactly the target width, so the machine overlays the panel 1:1
   * (machine height then lands at 158 mm against a 150 mm panel).
   */
  widthM: 0.1,

  /** Native GLB dimensions, from info/ar-glb-handoff.md §2. Do not change — asset facts. */
  nativeWidthM: 1.3,
  nativeHeightM: 2.05,
  /** Model-space Z of the front glass plane. Used to push the glass onto the target plane. */
  frontGlassZ: 0.375,

  /** 'center' straddles the target symmetrically; 'bottom' puts the cabinet floor at the panel's bottom edge. */
  alignMode: 'center' as 'center' | 'bottom',

  /** Tuner output, anchor units, added on top of the chosen align mode. */
  alignOffset: { x: 0, y: 0, z: 0 },
  /** Tuner output, degrees. */
  alignRotationDeg: { x: 0, y: 0, z: 0 },

  /** SideShelves overhang the target by ~30 mm on the right at 1:1. Turn off if it reads badly. */
  showSideShelves: true,
};

// ---------------------------------------------------------------------------
// Hero can — WORLD space. See plan §6: this is where rev.1 was wrong by ~1024x.
// ---------------------------------------------------------------------------

export const HERO_CAN = {
  /**
   * Where the can settles, in metres in front of the CAMERA.
   * Converted to world units with worldUnitsPerMeter (= imagePxWidth / widthM), never with the
   * anchor factor. At 0.12 m the can subtends roughly a third of the screen height.
   * Lower this (down to ~0.06) for a more dramatic reveal.
   */
  distanceM: 0.12,

  /**
   * Keep the can at least this far IN FRONT of the target plane. Without this clamp a user who
   * leans closer than distanceM gets the can settling inside the cabinet, occluded by it.
   */
  minMarginM: 0.03,

  /** Animation timings baked into the Reward clip (handoff §4.7). Asset facts. */
  rewardHeroStartS: 0.7,
  rewardSettleS: 2.867,
  /** Signal_CanStanding steps 0.001 -> 1.0. Anything above this counts as "standing". */
  standingThreshold: 0.5,
};

// ---------------------------------------------------------------------------
// Tracking — MindAR constructor options (verified defaults in mind-ar@1.2.5 controller.js)
// ---------------------------------------------------------------------------

export const MINDAR = {
  maxTrack: 1,
  /** MUST stay 'no'. MindAR otherwise injects its own loading/scanning/error DOM over our screens. */
  uiLoading: 'no',
  uiScanning: 'no',
  uiError: 'no',
  /**
   * MindAR's one-euro filter: `cutOff = filterMinCF + filterBeta * |speed|`, and a larger cutOff
   * means LESS smoothing. Two things about it are visible only in the source
   * (`src/vendor/mindar/ui-fBadYuor.js`, and `controller-*.js` calls it with `Date.now()`):
   *
   *  - its time base is MILLISECONDS, so `filterMinCF` is effectively cycles per millisecond:
   *    0.001 behaves like ~1 Hz, not 0.001 Hz;
   *  - it filters the 16 raw matrix elements, whose scales differ by ~1000x (translation is in
   *    compiled-target pixels, ~1024 per 100 mm; the rotation elements are <= 1). One `beta`
   *    therefore cannot mean the same thing for both.
   *
   * The 2026-07-30 values (0.0005 / 20) tried to smooth here as well, and they are most of what the
   * "model lags behind then snaps back" report is made of. The filter's own derivative term is
   * smoothed at a fixed 1 Hz, so at the start of every movement — and at every direction reversal
   * of a shake — the cutoff collapses to this floor and the pose barely moves for several updates;
   * halving the floor doubled that. `beta = 20` additionally left the rotation elements smoothed
   * while translation ran unfiltered, so the pose was not even filtered rigidly.
   *
   * Back to near-stock, deliberately: smoothing now happens in ONE place, POSE below, where it is
   * frame-rate independent, expressed in m/s, and bounded by a hard drift ceiling.
   */
  filterMinCF: 0.002,
  filterBeta: 1000,
  /** Frames of consistent detection before "found" fires. */
  warmupTolerance: 5,
  /** Frames of missed detection before "lost" fires. Raise to ride out brief occlusions. */
  missTolerance: 5,

  /**
   * Vertical field of view of the camera stream, degrees. THE calibration knob for "the machine
   * looks tilted when I move sideways".
   *
   * MindAR does not measure the camera — it hardcodes 45° (see `src/ar/intrinsics.ts` for the
   * source line and the consequences). A phone's rear camera is 60-66° over the long axis of a
   * portrait stream, and the resulting focal-length error is absorbed by the fitted pose as a
   * rotation and depth bias that varies with the viewing angle. The card itself still registers —
   * only geometry standing off the card plane is drawn wrong, which is the whole machine.
   *
   * 45 = MindAR's own assumption = no override, no vendor state touched. Anything else re-writes
   * the intrinsics into the tracker, the worker AND the render camera at session start, so it
   * REQUIRES A RELOAD.
   *
   * Calibrate, do not guess: aim at the card from a ruler-measured distance, read `cameraToTargetMm`
   * off the debug HUD, and use the debug panel's `fov calib` chip (or
   * `calibratedVerticalFovDeg()`). The estimated depth scales exactly with the focal error, so one
   * measurement is enough.
   */
  cameraVerticalFovDeg: 45,
};

// ---------------------------------------------------------------------------
// Pose smoothing and reveal
// ---------------------------------------------------------------------------

export const POSE = {
  /**
   * Speed-adaptive damping. `cutoffHz = minCutoffHz + betaPerMps * speed`, and the per-frame
   * blend is `alpha = 1 - exp(-2*PI*cutoffHz*dt)` — frame-rate independent.
   *
   * Fixed damping is the wrong shape for this problem: it lags MOST exactly when the phone is
   * moving, which reads as the model sliding off the target and then catching up. Adapting the
   * cutoff to speed inverts that — heavy smoothing while still (where jitter is visible), almost
   * none while moving (where lag is visible and noise is not).
   */
  /**
   * Smoothing while the pose is essentially still. Lower = steadier, but slower to respond.
   * Deliberately unchanged while MINDAR's filter was opened up: rest jitter was never the
   * complaint, and this stage is now the only thing rejecting it.
   */
  minCutoffHz: 1.2,
  /**
   * How fast smoothing releases with motion. Higher = tighter tracking during movement.
   * At 120, an ordinary 0.15 m/s hand movement is tracked at ~87% per frame (it was ~53% at 40,
   * which is the trailing the device report described) while a still phone still gets ~12%.
   */
  betaPerMps: 120,
  /**
   * Time constant for the internal speed estimate. Too small and the speed term itself flickers.
   * Sampled per POSE UPDATE, not per render frame — see `session.ts`.
   *
   * Raised 0.05 -> 0.12 on 2026-08-04. It also damps `trackedVelocity`, which prediction multiplies,
   * so at 0.05 (an alpha of 0.63 against a 50 ms pose interval) the velocity was barely filtered and
   * its noise reached the screen scaled by the look-ahead. Simulated over the real filter functions
   * at 60 fps / 20 Hz poses / 0.15 m/s pan: rendered jitter p95 1.20 mm -> 0.98 mm with the mean lag
   * unchanged to 0.01 mm. The gain is linear in tracker noise, so the ratio holds whatever the
   * device's actual noise turns out to be.
   */
  speedSmoothingS: 0.12,

  /**
   * Hard ceiling on how far the rendered pose may trail the tracked pose, in metres of real-world
   * distance. Whatever the filter decides, the model can never visibly separate from the target
   * by more than this. 0 disables the clamp.
   *
   * This ceiling IS the visible gap budget: at a 1:1 overlay the machine renders as wide as the
   * target, so the old 4 mm was 4% of the machine's width — plainly visible, and exactly what was
   * reported. Note it is measured against the TRACKED pose, so it bounds our own contribution
   * only; MindAR's latency is upstream of it and is what `predictionMs` addresses.
   */
  maxDriftM: 0.0015,
  /** Same ceiling for orientation, degrees. 0.6° moves a 100 mm machine's edge by ~0.5 mm. */
  maxDriftDeg: 0.6,

  /**
   * Forward prediction, milliseconds. 0 = off.
   *
   * The tracker's own latency (one processing iteration, plus holding the pose still between
   * updates) is the one term no smoothing can remove — see `predictionHorizon()`. This draws the
   * model where the target will be rather than where the tracker last saw it.
   *
   * Keep this SMALL and let the age term do the adapting. `predictionHorizon()` looks ahead by
   * `poseAge + predictionMs`, so the staleness that actually varies between devices and modes is
   * already measured per frame; this constant is only the tracker's irreducible latency on top.
   * It cannot be 0 — the function early-returns at `latency <= 0`, which kills the age term too.
   *
   * Both readings behind this number came off the SAME phone on 2026-08-03, and they disagreed:
   *   - cover screen, no HUD: the pose looked frozen for 4-8 frames of a 60 fps capture
   *     (~70-130 ms) and the machine trailed the card by ~50 mm during a ~0.5 m/s pan;
   *   - inner screen, HUD on: `poseHz` 58-60, `stalePct` 0-3 %, `ageMs` 0-13 — no staleness at all.
   * A constant sized for the first (70) overshoots badly on the second. 15 covers neither case by
   * itself, which is the point: the age term supplies the rest exactly when it is real.
   *
   * It also buys JITTER, which is the other reason to keep it small. The look-ahead multiplies
   * `trackedVelocity` — a pose-to-pose difference carrying the tracker's per-frame noise, damped
   * only by `speedSmoothingS` — so residual velocity noise reaches the screen scaled by this
   * value. A slit-scan of the two recordings above shows it directly: at 70 the rendered edges
   * serrate at roughly 6 mm while the camera image behind them stays smooth, and at 0 they were
   * smooth curves. The wobble scales with this number, so 15 leaves about a fifth of it.
   *
   * Position only. Extrapolating orientation as well rings visibly when a shake reverses.
   */
  predictionMs: 15,
  /**
   * Ceiling on how far prediction may move the model, metres. 0 disables the ceiling.
   *
   * Only binds while the pose is actually stale — with `ageMs` near 0 the look-ahead is
   * `0.015 * speed`, far under this. So size it for the dropout case, not the healthy one: while
   * MindAR re-emits a stale pose the velocity estimate is whatever it was before the dropout, and
   * this is how far that stale velocity may throw the model before the horizon cap below stops it.
   */
  maxPredictionM: 0.02,
  /**
   * Ceiling on the look-ahead (pose age + predictionMs), milliseconds. The pose age climbs while
   * MindAR re-emits a stale pose after a tracking dropout; without this, prediction would run away
   * during precisely that window.
   */
  maxPredictionHorizonMs: 120,

  /**
   * Snap instead of damping when the target is re-acquired. Without this the model visibly slides
   * in from wherever it was last seen, which reads as a glitch rather than as tracking.
   */
  snapOnReacquire: true,

  /** Reveal animation when the machine first appears, milliseconds. 0 = pop in instantly. */
  revealMs: 520,
  /** Scale the machine grows from during the reveal. */
  revealFromScale: 0.72,
};

// ---------------------------------------------------------------------------
// Rendering / lighting
// ---------------------------------------------------------------------------

export const RENDER = {
  /**
   * MindAR calls setPixelRatio(window.devicePixelRatio) uncapped, and forces antialias: true
   * (a context-creation flag we cannot change). Capping DPR is therefore the primary perf lever.
   */
  pixelRatioCap: 2,
  /** PMREM blur for RoomEnvironment. Required — without an env map the metal parts render black. */
  envPmremSigma: 0.04,
  dirLight: { color: 0xffffff, intensity: 1.6 },
  ambientLight: { color: 0xbfd8ff, intensity: 0.35 },
  /** The asset was authored for NoToneMapping. 'aces' needs exposure ~1.25 or the neon washes out. */
  toneMapping: 'none' as 'none' | 'aces',
  toneExposure: 1.0,
  /** Emissive boost for QR_Screen / SubScreen so they stay readable in a dark AR scene (handoff §6). */
  emissiveIntensity: 1.15,
};

// ---------------------------------------------------------------------------
// Interaction
// ---------------------------------------------------------------------------

export const INTERACTION = {
  /** Raycast-only enlargement of Coin_Hitbox. Changes nothing on screen. */
  hitboxScale: 2.0,
  /**
   * When true, a tap is rejected if an OPAQUE part of the machine sits in front of the coin.
   * (Transparent parts — the front glass and the display cans — are ignored, or the glass would
   * block every tap.)
   *
   * Default OFF deliberately. At 1:1 scale the machine renders ~100 mm wide, so the coin is a
   * small target and the demo's most likely failure is a user who cannot hit it. Generous hit
   * detection is worth more here than punishing a tap on a visually occluded coin.
   */
  hitboxOcclusionCheck: false,
  /** pointerdown -> pointerup travel above this is a drag, not a tap. */
  tapMoveTolerancePx: 6,
};

// ---------------------------------------------------------------------------
// Timing (milliseconds unless noted)
// ---------------------------------------------------------------------------

export const TIMING = {
  /** PLAY: delay before the "find the coin" caption appears. */
  coinHintDelayMs: 1000,
  screenFadeMs: 240,
  /** MockPrizeService latency range [min, max]. */
  prizeLatencyMs: [200, 500] as [number, number],
  /** Abort draw() after this long and show the failure path. */
  prizeTimeoutMs: 8000,
  /** Handoff §4.7: let the burst clear before Idle resumes. */
  idleResumeDelayMs: 1200,
  /** Any state returns to BOOT after this much inactivity. null disables. */
  idleTimeoutMs: 90_000 as number | null,
  /**
   * Mixer delta clamp, in SECONDS. Without it, returning from a backgrounded tab feeds
   * mixer.update() the whole elapsed wall time and teleports Reward to its end — the coupon
   * sheet then appears while the can is still at the outlet.
   */
  maxFrameDtS: 1 / 30,
};

// ---------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------

export const ANIMATION = {
  /**
   * 'onTargetFound' plays Idle together with Intro so the machine looks alive from the first
   * frame (handoff §4.4). 'onJoinGate' holds the display coins still until the start button.
   */
  idleStart: 'onTargetFound' as 'onTargetFound' | 'onJoinGate',
  /** Clip names. Looked up by name — the file order is Reward, Idle, Intro. Asset facts. */
  clip: { intro: 'Intro', idle: 'Idle', reward: 'Reward' },
};

// ---------------------------------------------------------------------------
// Prize
// ---------------------------------------------------------------------------

export const PRIZE = {
  mode: 'fixed' as 'fixed' | 'random',
  fixedId: 'A' as FigureId,
};

// ---------------------------------------------------------------------------
// Assets and entry point
// ---------------------------------------------------------------------------

/**
 * `import.meta.env.BASE_URL` — '/' in dev, '/sar/npay-ar/' in the deployed build (vite.config.ts
 * `DEPLOY_BASE`). Always ends with a slash, so the paths below are appended without one.
 *
 * These are plain string literals, and Vite rewrites URLs only in HTML attributes and inside
 * `new URL(..., import.meta.url)` — never in a literal. A hardcoded '/assets/...' therefore
 * resolves against the DOMAIN root once deployed under a sub-path and 404s. Prefix explicitly.
 */
const BASE = import.meta.env.BASE_URL;

export const COUPON = {
  /**
   * `{code}` is replaced with the draw's coupon code. This string reaches the device's file system
   * and the share sheet, so keep it ASCII — some Android download managers mangle Hangul filenames.
   */
  fileName: 'naver-pet-coupon-{code}.png',
  /**
   * iOS route (plan §9). `layer` shows the coupon full-screen so the user can long-press →
   * "사진에 추가"; `share` opens the OS share sheet instead, which is one tap but depends on
   * `navigator.canShare({files})` being honoured. Android and desktop always use `<a download>`
   * and ignore this.
   */
  iosMode: 'layer' as 'layer' | 'share',
  /** How long the "저장되었습니다" toast stays up after an Android download. */
  savedToastMs: 2600,
};

export const ASSET = {
  glb: `${BASE}assets/ar_vending_machine.glb`,
  mind: `${BASE}assets/target/targets.mind`,
  targetMeta: `${BASE}assets/target/target.meta.json`,
  qr: `${BASE}assets/qr/app-qr.png`,
  coupon: `${BASE}assets/coupon/coupon-sample.png`,
};

/**
 * BUILD-TIME ONLY. The QR image is generated from this value by tools/make-qr.mjs, so editing it
 * in the settings page changes nothing — schema.ts marks it read-only.
 *
 * Public demo entry point. Keep the trailing slash: without it nginx answers the directory with a
 * 301 and every scan pays an extra round trip before the camera gate. Must stay in sync with
 * `DEPLOY_BASE` in vite.config.ts and with the nginx snippet deploy/nginx/sar.conf — re-run
 * `npm run asset:qr` after any change, or the printed QR points at the previous host.
 */
export const APP_URL = 'https://ki.blot-i.co.kr/sar/npay-ar/';

// ---------------------------------------------------------------------------
// Debug entry
// ---------------------------------------------------------------------------

export const DEBUG = {
  /** URL parameter: ?debug=1 */
  param: 'debug',
  /** Taps on the BOOT logo to open the panel. */
  tapCount: 5,
  /** Require a long press first — a bare 5-tap is reachable by an idle visitor at a booth. */
  requireLongPress: true,
  longPressMs: 700,

  /**
   * Pose recorder (plan `doc/plan/02_pose-recorder.md`). Records a labelled run on the device and
   * renders a report to the clipboard, so "it jitters / it twists / it fails far away" becomes a
   * number that points at one stage of the pipeline instead of three possible ones.
   */
  recorder: {
    /** Auto-stop, seconds. Long enough for a deliberate movement, short enough to hold a pose. */
    durationS: 20,
    /** Rate of the downsampled trace in the report. Higher = a longer paste. */
    seriesHz: 5,
    /**
     * A single pose update turning by more than this is the TRACKER being wrong, not the phone
     * moving: at 30°/s — already a brisk turn — one 20 Hz update covers 1.5°.
     */
    jumpDeg: 3,
    /** Same, for translation. 4 mm in one update at 0.3 m is not a hand movement. */
    jumpMm: 4,
    /** Below this the phone counts as held still, which is the only place jitter is visible. */
    restSpeedMps: 0.02,
    /** Cap on each printed event list; the count is still reported in full. */
    maxEvents: 12,
  },
};

// ---------------------------------------------------------------------------
// Copy — every user-visible string, including error and recovery paths.
// ---------------------------------------------------------------------------

export const COPY = {
  boot: {
    titleEn: 'NAVER PET',
    titleKo: '네이버 펫',
    start: 'START',
  },
  unsupported: {
    heading: '이 브라우저에서는 AR을 실행할 수 없어요',
    inAppBrowser: '카카오톡·네이버 앱 안에서는 카메라를 쓸 수 없습니다.\nSafari 또는 Chrome에서 열어주세요.',
    noWebgl: '이 기기에서는 3D 기능을 지원하지 않습니다.',
    insecure: '보안 연결(HTTPS)이 아니어서 카메라를 열 수 없습니다.',
    noCamera: '사용할 수 있는 카메라를 찾지 못했습니다.',
    cameraBusy: '다른 앱이 카메라를 사용 중입니다. 해당 앱을 종료한 뒤 다시 시도해 주세요.',
    copyUrl: '주소 복사',
    copied: '복사되었습니다',
  },
  permission: {
    prompt: 'AR을 사용하려면\n권한을 허용해 주세요.',
    deniedOnce: '카메라 권한이 필요합니다.\n권한을 허용해야 게임을 시작할 수 있어요.',
    retry: '다시 시도',
    /**
     * A denial is remembered per origin, so an in-page retry can never re-prompt. These are the
     * only paths that actually recover.
     */
    deniedStickyIos: '카메라 권한이 차단되어 있습니다.\n주소창의 「아A」 → 웹사이트 설정 → 카메라 → 허용으로 바꾼 뒤\n페이지를 새로고침해 주세요.',
    deniedStickyAndroid: '카메라 권한이 차단되어 있습니다.\n주소창의 자물쇠 아이콘 → 권한 → 카메라를 허용으로 바꾼 뒤\n페이지를 새로고침해 주세요.',
    reload: '새로고침',
  },
  scanning: {
    line1: '노란 패널을 화면 안에 맞춰주세요!',
    line2: '숨은 코인을 찾아 실제 NEW 캐릭터 GET!',
    loading: '준비 중…',
  },
  intro: {
    caption: '코인이 자판기로 들어가고 있어요…',
  },
  joinGate: {
    heading: '코인이 숨는 곳을 잘 찾아보세요!',
    start: '시작하기',
  },
  play: {
    hint: '코인을 찾아 눌러보세요!',
  },
  resolving: {
    caption: '결과를 확인하는 중…',
    failed: '결과를 가져오지 못했어요.',
    retry: '다시 시도',
  },
  result: {
    brandEn: 'NAVER PAY',
    brandKo: '네이버페이',
    subhead: '오늘의 쿠폰이 도착했어요!',
    /** Reference screen wording, kept available: implies a once-per-day limit (out of scope). */
    exhausted: '오늘 기회는 모두 소진되었습니다.\n내일 또 이용해 주세요!',
    download: '쿠폰 다운로드',
    /** <a download> and the long-press fallback give no completion signal — the user tells us. */
    confirmSaved: '저장 완료',
    longPressGuide: "이미지를 길게 눌러 '사진에 추가'를 선택하세요",
    saveFailed: '저장에 실패했어요. 다시 시도해 주세요.',
    /** Android toast. `<a download>` is silent, so without this nothing on screen changes. */
    saved: '쿠폰이 저장되었습니다',
    /** iOS save layer. */
    saveLayerHeading: '쿠폰을 저장해 주세요',
    close: '닫기',
    /** Read by screen readers on the coupon image; also the alt text if the image fails to load. */
    couponAlt: '쿠폰 이미지',
    preparing: '쿠폰을 준비하고 있어요…',
    retry: '다시 시도',
  },
  done: {
    brandEn: 'NAVER PET',
    brandKo: '네이버페이',
    badge: '이벤트 참여 완료',
    heading: '이벤트 참여가 완료되었습니다.',
    tapToRestart: '화면을 터치하면 처음으로',
  },
  common: {
    /** Shown when the target leaves frame — suppressed during RESULT and DONE (plan §7). */
    targetLost: '노란 패널을 화면 안에 다시 맞춰주세요',
    landscapeBlocker: '세로로 돌려주세요',
    /** iOS drops the camera track on backgrounding; resuming needs a fresh gesture. */
    cameraSuspended: '화면을 눌러 카메라를 다시 켜주세요',
    configCorrupt: '저장된 설정을 읽지 못해 기본값으로 시작했습니다.',
    loadFailed: '리소스를 불러오지 못했습니다. 새로고침해 주세요.',
    idleTimeout: '오래 사용하지 않아 처음으로 돌아갑니다.',
  },
};

// ---------------------------------------------------------------------------

export const DEFAULTS = {
  TARGET,
  MACHINE,
  HERO_CAN,
  POSE,
  MINDAR,
  RENDER,
  INTERACTION,
  TIMING,
  ANIMATION,
  PRIZE,
  COUPON,
  ASSET,
  APP_URL,
  DEBUG,
  COPY,
};

/**
 * The runtime config shape.
 *
 * Note the deliberate absence of `as const` on the groups above: the settings page and the debug
 * tuner must be able to assign new values, and `as const` would pin every number to its own
 * literal type (`widthM: 0.1` rather than `widthM: number`). Unions that genuinely are closed —
 * `alignMode`, `toneMapping`, `PRIZE.mode` — are declared explicitly with `as` instead.
 */
export type Config = typeof DEFAULTS;
